import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { type StayUpdate } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { ErrorState, LoadingState } from '../../components/ui/state';
import { api } from '../../shared/api';
import { StayFields, StayFormShell, stayFormSchema, type StayFormInput, type StayFormOutput, useStayLookups } from './StayCreatePage';

export function StayEditPage() {
  const { stayId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stayQuery = useQuery({ queryKey: ['stay', stayId], queryFn: () => api.stays.get(stayId), enabled: Boolean(stayId) });
  const form = useForm<StayFormInput, unknown, StayFormOutput>({ resolver: zodResolver(stayFormSchema), defaultValues: { customerId: '', dogId: '', dogIds: [], checkInDate: '', checkOutDate: '', serviceIds: [], dogServiceIds: {}, dogCare: {}, kennel: '', room: '', notes: '' } });
  const selectedCustomerId = form.watch('customerId');
  const lookups = useStayLookups(selectedCustomerId);
  useEffect(() => {
    if (stayQuery.data) {
      const dogIds = stayQuery.data.dogIds?.length ? stayQuery.data.dogIds : [stayQuery.data.dogId];
      form.reset({
        ...stayQuery.data,
        dogIds,
        serviceIds: stayQuery.data.serviceIds ?? [],
        dogServiceIds: stayQuery.data.dogServiceIds ?? Object.fromEntries(dogIds.map((dogId) => [dogId, stayQuery.data.serviceIds ?? []])),
        dogCare: stayQuery.data.dogCare ?? {},
      });
    }
  }, [stayQuery.data, form]);
  const updateStay = useMutation({
    mutationFn: (values: StayFormOutput) => {
      const { customerId, dogId, ...input } = values;
      void customerId;
      void dogId;
      return api.stays.update(stayId, input satisfies StayUpdate);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stays'] });
      navigate('/admin/stays');
    },
  });
  if (stayQuery.isLoading) return <LoadingState label="Cargando hotel" />;
  if (stayQuery.isError) return <ErrorState message={stayQuery.error.message} />;
  return (
    <StayFormShell title="Editar hotel">
      <form className="grid gap-3" onSubmit={form.handleSubmit((values) => updateStay.mutate(values))}>
        <StayFields form={form} {...lookups} />
        {updateStay.isError ? <p className="text-sm text-rose-600">{updateStay.error.message}</p> : null}
        <Button type="submit" disabled={updateStay.isPending}><Save className="h-4 w-4" aria-hidden="true" />Guardar cambios</Button>
      </form>
    </StayFormShell>
  );
}
